<template>
  <div class="h-full">
    <div class="flex w-full flex-col gap-6">
      <div class="grid grid-cols-3 gap-2">
        <InfuseButton
          color="secondary"
          v-if="!model?.active"
          v-permission="{ id: userPermissions.ACTIVATE, hide: false }"
          @click="onActivateUser"
          :loading="isActivateUserLoading"
        >
          {{ t('iam.users.header.activate') }}
        </InfuseButton>
        <InfuseButton
          color="alter"
          v-else
          v-permission="{ id: userPermissions.DEACTIVATE, hide: false }"
          :loading="isDeactivateUserLoading"
          @click="onDeactivateUser"
        >
          {{ t('iam.users.header.deactivate') }}
        </InfuseButton>

        <InfuseButton
          color="secondary"
          @click="onForceChangePassword"
          v-permission="{ id: userPermissions.FORCE_CHANGE_PASSWORD, hide: false }"
          :loading="isForceChangePasswordLoading"
        >
          {{ t('iam.users.header.force_change_password') }}
        </InfuseButton>

        <InfuseButton
          v-if="!isEntraAuth"
          color="secondary"
          v-permission="{ id: userPermissions.FORGOT_PASSWORD, hide: false }"
          :loading="isForgotPasswordLoading"
          @click="onForgotPassword"
        >
          {{ t('iam.users.header.forgot_password') }}
        </InfuseButton>
      </div>
      <div class="grid grid-cols-2 gap-4 text-[0.8rem]">
        <InfusePropertyValue :title="t('iam.users.id')" :title-icon="{ type: 'far', name: 'hashtag' }">
          {{ model?.id }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(model?.id || '')"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('iam.users.externalId')" :title-icon="{ type: 'far', name: 'hashtag' }">
          {{ model?.externalId }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(model?.externalId || '')"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('iam.users.email')" :title-icon="{ type: 'fal', name: 'envelope' }">
          {{ model?.email }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(model?.email || '')"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('iam.users.name')" :title-icon="{ type: 'fal', name: 'user' }">
          {{ model?.name || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('iam.users.roles')" :title-icon="{ type: 'fal', name: 'address-card' }">
          <ul v-if="model?.roleLabels?.length">
            <li v-for="(role, idx) in model?.roleLabels || []" :key="`role-${role.value}-${idx}`">
              <router-link :to="`/role/${role.value}`" class="text-primary-500 hover:underline">
                {{ role.label }}
                <font-awesome-icon :icon="['far', 'arrow-right-from-bracket']" class="ml-1" />
              </router-link>
            </li>
          </ul>
          <span v-else>-</span>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('iam.users.createdAt')" :title-icon="{ type: 'far', name: 'clock' }">
          {{ model?.createdAt ? formatDate(model.createdAt) : '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('iam.users.modifiedAt')" :title-icon="{ type: 'far', name: 'clock' }">
          {{ model?.modifiedAt ? formatDate(model.modifiedAt) : '-' }}
        </InfusePropertyValue>
      </div>
    </div>
  </div>

  <ForceChangePasswordUserModal
    v-if="showForceChangePasswordModal"
    :user="model"
    :loading="isForceChangePasswordLoading"
    @close="showForceChangePasswordModal = false"
    @mutate="confirmForceChangePassword"
  />

  <InfuseConfirmModal
    v-if="showActivateConfirmModal"
    :action-title="t('iam.users.header.activate')"
    :verification-text="'activate'"
    :loading="isActivateUserLoading"
    @close="showActivateConfirmModal = false"
    @confirm="confirmActivateUser"
  />
  <InfuseConfirmModal
    v-if="showDeactivateConfirmModal"
    :action-title="t('iam.users.header.deactivate')"
    :verification-text="'deactivate'"
    :loading="isDeactivateUserLoading"
    @close="showDeactivateConfirmModal = false"
    @confirm="confirmDeactivateUser"
  />
  <InfuseConfirmModal
    v-if="showForgotPasswordConfirmModal && !isEntraAuth"
    :action-title="t('iam.users.header.forgot_password')"
    :action-description="t('iam.users.forgot_password_description')"
    :verification-text="'infuse'"
    :loading="isForgotPasswordLoading"
    @close="showForgotPasswordConfirmModal = false"
    @confirm="confirmForgotPassword"
  />
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { userPermissions } from '@infuse/api/iam/user';
import { vPermission } from '@infuse/iam/permission/directives';
import { useAssetLevel } from '@infuse/iam/composables';
import {
  useActivateUser,
  useDeactivateUser,
  useForceChangePasswordUser,
  useForgotPasswordUser
} from '@infuse/iam/user/mutations';
import { User } from '@infuse/iam/user/type';
import { ForceChangePasswordUserModal } from '@infuse/iam/user/ui';
import { InfuseButton, InfuseConfirmModal, InfuseCopy, InfusePropertyValue } from '@infuse/ui/components';
import { formatDate } from '@infuse/ui/utils';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthenticator, EntraAuthenticator } from '@infuse/auth/services';

const { t } = useI18n();
const { getLevelTranslation } = useAssetLevel();

const authenticator = useAuthenticator();
const isEntraAuth = computed(() => authenticator instanceof EntraAuthenticator);

const { mutateAsync: deactivateUser, isPending: isDeactivateUserLoading } = useDeactivateUser();
const { mutateAsync: activateUser, isPending: isActivateUserLoading } = useActivateUser();
const { mutateAsync: forceChangePassword, isPending: isForceChangePasswordLoading } = useForceChangePasswordUser();
const { mutateAsync: forgotPassword, isPending: isForgotPasswordLoading } = useForgotPasswordUser();

const props = defineProps<{
  model: User;
  isLoading: boolean;
}>();

interface Emits {
  (e: 'reload-model'): void;
}
defineEmits<Emits>();

const levelTranslation = computed(() => (props.model ? getLevelTranslation(props.model) : '-'));

const showForceChangePasswordModal = ref(false);
const showActivateConfirmModal = ref(false);
const showDeactivateConfirmModal = ref(false);
const showForgotPasswordConfirmModal = ref(false);

async function onDeactivateUser() {
  showDeactivateConfirmModal.value = true;
}

async function onActivateUser() {
  showActivateConfirmModal.value = true;
}

async function onForceChangePassword() {
  showForceChangePasswordModal.value = true;
}

async function onForgotPassword() {
  showForgotPasswordConfirmModal.value = true;
}

async function confirmDeactivateUser() {
  await deactivateUser({ id: props.model.id });
  showDeactivateConfirmModal.value = false;
}

async function confirmActivateUser() {
  await activateUser({ id: props.model.id });
  showActivateConfirmModal.value = false;
}

async function confirmForgotPassword() {
  await forgotPassword({ id: props.model.id });
  showForgotPasswordConfirmModal.value = false;
}

async function confirmForceChangePassword(action: { tempPassword: string }) {
  await forceChangePassword({ id: props.model.id, tempPassword: action.tempPassword });
  showForceChangePasswordModal.value = false;
}
</script>
